// /settings/email — Email Delivery (Gmail / custom SMTP)
//
// The tenant's own sending account (plan §5, §6, §9). A saved account is not a
// usable one: every save re-runs the SMTP handshake and mails a 6-digit code to
// the mailbox itself, and only a VERIFIED row is ever used for sending. Health
// is a second, independent axis — a VERIFIED account whose App Password was
// revoked yesterday is still VERIFIED, and FAILING is what the tenant must act
// on, so it gets a banner above everything else.
//
// OWNER writes. SUPER_ADMIN / ADMIN / MODERATOR get a read-only view for
// support. MANAGER / CASHIER are blocked at the route level (see router.tsx).

import { useCallback, useEffect, useState } from 'react';
import { PageContainer, Button, Skeleton, ErrorBoundary, useToast } from '@bbpos/ui';
import { emailTransportService } from '@/services/email-transport.service';
import { EmailHealthBanner } from '@/components/email/EmailHealthBanner';
import { EmailStatusPill } from '@/components/email/EmailStatusPill';
import { EmailTransportForm } from '@/components/email/EmailTransportForm';
import { EmailVerificationCard } from '@/components/email/EmailVerificationCard';
import { SendTestEmailCard } from '@/components/email/SendTestEmailCard';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import type { EmailTransportConfigMasked, UpsertEmailTransportInput } from '@bbpos/types';
import { SMTP_PROVIDER_LABELS } from '@bbpos/types';

const APP_PASSWORD_URL = 'https://myaccount.google.com/apppasswords';

function formatStamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString('en-IN');
}

export function EmailConfigPage() {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.role ?? s.profile?.role ?? '');
  const loginEmail = useAuthStore((s) => s.user?.email ?? s.profile?.email ?? '');
  const canEdit = role === 'OWNER';
  const isPlatform = role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'MODERATOR';

  const [config, setConfig] = useState<EmailTransportConfigMasked | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disabling, setDisabling] = useState(false);
  // Expiry of the code this session just triggered — the GET shape carries
  // `verification_sent_at` but not the TTL, so it only exists in memory.
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConfig(await emailTransportService.getConfig());
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setConfig(null);
      } else {
        toast({
          variant: 'error',
          title: 'Failed to load email settings',
          description: err instanceof ApiError ? err.message : 'Please try again',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    document.title = 'Email Delivery — Orio360 Admin';
  }, []);

  const handleSave = async (input: UpsertEmailTransportInput) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { config: saved, verification } = await emailTransportService.upsertConfig(input);
      setConfig(saved);
      if (verification.sent) {
        setExpiresAt(verification.expires_at ?? null);
        toast({
          variant: 'success',
          title: 'Verification code sent',
          description: `Check ${verification.sent_to ?? saved.email} for a 6-digit code.`,
        });
      } else {
        setExpiresAt(null);
        const message = verification.error ?? 'The mail server rejected these credentials.';
        setSaveError(message);
        toast({ variant: 'warning', title: 'Could not connect', description: message });
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Save failed';
      setSaveError(message);
      toast({ variant: 'error', title: 'Could not save', description: message });
    } finally {
      setSaving(false);
    }
  };

  const handleVerified = (verified: EmailTransportConfigMasked) => {
    setConfig(verified);
    setExpiresAt(null);
    setSaveError(null);
    toast({
      variant: 'success',
      title: 'Sender verified',
      description: `Orio360 will now send your email from ${verified.email}.`,
    });
  };

  const handleDisable = async () => {
    if (
      !window.confirm(
        'Disable this sending account? Invoices and notifications will stop going out until you verify an account again.',
      )
    ) {
      return;
    }
    setDisabling(true);
    try {
      await emailTransportService.disableConfig();
      toast({ variant: 'success', title: 'Sending account disabled' });
      setExpiresAt(null);
      await load();
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not disable',
        description: err instanceof ApiError ? err.message : 'Please try again',
      });
    } finally {
      setDisabling(false);
    }
  };

  // A PENDING row whose handshake failed has no code out there to enter
  // (`verification_sent_at` stays NULL — plan §5.2), so it gets the form and
  // the error, not the code box.
  const isPending =
    config?.verification_status === 'PENDING' && Boolean(config.verification_sent_at);
  const isVerified = config?.verification_status === 'VERIFIED';

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-20px_rgba(79,70,229,0.55)] animate-fade-in">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-brand-100">
                Integrations · Notifications
              </div>
              <h1 className="text-2xl font-bold text-white">Email Delivery</h1>
              <p className="mt-1 max-w-2xl text-sm text-brand-100">
                Send invoices and notifications from your own mailbox — a Gmail account with an App
                Password, or any SMTP server. Passwords are AES-256-GCM encrypted at rest and never
                shown again; we verify the account with a code before using it.
              </p>
            </div>
            <EmailGlyph />
          </div>
        </div>

        {/* Platform read-only banner */}
        {isPlatform && (
          <div
            className={cn(GLASS, 'mb-6 flex items-start gap-3 border-l-4 border-brand-500 p-4')}
            data-testid="platform-readonly-banner"
          >
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-500 text-xs font-bold text-white">
              i
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white">
                Platform read-only
              </div>
              You&apos;re viewing this tenant&apos;s sending account for support purposes. Only the
              tenant <span className="font-semibold">OWNER</span> can save, verify or disable it.
            </div>
          </div>
        )}

        {/* FAILING is the one state that must interrupt — it sits above everything */}
        <EmailHealthBanner
          config={config}
          client={emailTransportService}
          canEdit={canEdit}
          onRechecked={load}
        />

        {/* Status cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <StatusCard config={config} loading={loading} />
          <SenderCard config={config} loading={loading} />
          <HealthCard config={config} loading={loading} />
          <LastTestCard config={config} loading={loading} />
        </div>

        {loading ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="space-y-6">
            {isPending && config && (
              <EmailVerificationCard
                config={config}
                client={emailTransportService}
                canEdit={canEdit}
                expiresAt={expiresAt}
                onVerified={handleVerified}
              />
            )}

            {isVerified && config && (
              <SendTestEmailCard
                config={config}
                client={emailTransportService}
                canEdit={canEdit}
                defaultTo={loginEmail}
                onTested={load}
              />
            )}

            {saveError && (
              <p
                className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:bg-danger-900/30 dark:text-danger-300"
                data-testid="email-save-error"
              >
                {saveError}
              </p>
            )}

            <EmailTransportForm
              existing={config}
              canEdit={canEdit}
              saving={saving}
              onSubmit={handleSave}
            />

            {config && config.verification_status !== 'DISABLED' && canEdit && (
              <div className={cn(GLASS_FORM, 'flex flex-wrap items-center gap-3 p-5')}>
                <div className="mr-auto text-sm text-neutral-600 dark:text-neutral-300">
                  <div className="font-semibold text-neutral-900 dark:text-white">
                    Stop using this account
                  </div>
                  Outbound email stops until another account is verified.
                </div>
                <Button variant="secondary" onClick={handleDisable} loading={disabling}>
                  Disable sending
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Setup help */}
        <div className={cn(GLASS, 'mt-6 border-l-4 border-indigo-500 p-5')}>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-500 text-xs font-bold text-white">
              1→3
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="mb-2 font-semibold text-neutral-900 dark:text-white">
                Setup in 3 steps
              </div>
              <ol className="list-inside list-decimal space-y-1">
                <li>
                  For Gmail: turn on 2-Step Verification, then create a 16-letter App Password at{' '}
                  <a
                    href={APP_PASSWORD_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-brand-600 hover:underline dark:text-brand-400"
                  >
                    myaccount.google.com/apppasswords
                  </a>
                  . For any other mail server, use its host, port and mailbox password.
                </li>
                <li>Save — we connect over TLS and mail a 6-digit code to that same mailbox.</li>
                <li>Enter the code. Once verified, send a test email to a different inbox.</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Security notice */}
        <div className={cn(GLASS, 'mt-4 border-l-4 border-success-500 p-5')}>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-success-500 text-xs font-bold text-white">
              SEC
            </div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="mb-1 font-semibold text-neutral-900 dark:text-white">
                Encrypted, and never plaintext on the wire
              </div>
              Passwords are AES-256-GCM encrypted at rest and never returned by the API. Every
              connection uses implicit TLS or mandatory STARTTLS. If delivery ever breaks, Orio360
              checks daily and tells you here and by email.
            </div>
          </div>
        </div>
      </PageContainer>
    </ErrorBoundary>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────

function CardShell({
  icon,
  gradient,
  label,
  children,
}: {
  icon: string;
  gradient: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(GLASS, 'p-5 transition-all duration-200 hover:shadow-lg')}>
      <div className="mb-3 flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br text-xs font-bold tracking-wider text-white',
            gradient,
          )}
          aria-hidden="true"
        >
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
      </div>
      {children}
    </div>
  );
}

function StatusCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  const gradient = !config
    ? 'from-neutral-400 to-neutral-500'
    : config.verification_status === 'VERIFIED'
      ? 'from-success-500 to-teal-500'
      : config.verification_status === 'PENDING'
        ? 'from-warning-500 to-amber-500'
        : 'from-neutral-400 to-neutral-500';
  return (
    <CardShell icon="AUTH" gradient={gradient} label="Status">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <div className="space-y-2">
          <EmailStatusPill config={config} />
          {config ? (
            <>
              <div className="font-mono text-xs text-neutral-500 dark:text-neutral-400">
                {SMTP_PROVIDER_LABELS[config.provider]} · {config.smtp_host}:{config.smtp_port}
              </div>
              <div className="text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
                Updated {formatStamp(config.updated_at) ?? '—'}
              </div>
            </>
          ) : (
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              No sending account yet. Outbound email is mocked in non-production and rejected in
              production until one is verified.
            </div>
          )}
        </div>
      )}
    </CardShell>
  );
}

function SenderCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  return (
    <CardShell icon="FROM" gradient="from-violet-500 to-pink-500" label="Sender">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : config ? (
        <>
          <div className="mb-2 break-all rounded-md bg-neutral-50 p-2 font-mono text-sm text-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-200">
            {config.from_name ? `${config.from_name} <${config.email}>` : config.email}
          </div>
          {config.reply_to_email && (
            <div className="break-all font-mono text-xs text-neutral-500 dark:text-neutral-400">
              Reply-To: {config.reply_to_email}
            </div>
          )}
          <div className="mt-2 font-mono text-xs text-neutral-400 dark:text-neutral-500">
            Password {config.password_masked}
          </div>
        </>
      ) : (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          Your From address appears here once an account is saved.
        </div>
      )}
    </CardShell>
  );
}

function HealthCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  const status = config?.health_status ?? 'UNKNOWN';
  const gradient =
    status === 'OK'
      ? 'from-success-500 to-teal-500'
      : status === 'FAILING'
        ? 'from-danger-500 to-warning-500'
        : 'from-neutral-400 to-neutral-500';
  const checkedAt = formatStamp(config?.health_checked_at);
  return (
    <CardShell icon="HLTH" gradient={gradient} label="Delivery health">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <>
          <div className="text-lg font-bold text-neutral-900 dark:text-white">
            {status === 'OK' ? 'Healthy' : status === 'FAILING' ? 'Failing' : 'Not checked yet'}
          </div>
          {config?.health_error && status === 'FAILING' && (
            <div className="mt-2 break-all rounded-md bg-danger-50 p-2 font-mono text-[11px] text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
              {config.health_error}
            </div>
          )}
          <div className="mt-2 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            {checkedAt ? `Checked ${checkedAt}` : 'Checked daily at 5:00 AM IST'}
            {config && config.consecutive_failures > 0
              ? ` · ${config.consecutive_failures} consecutive failures`
              : ''}
          </div>
        </>
      )}
    </CardShell>
  );
}

function LastTestCard({
  config,
  loading,
}: {
  config: EmailTransportConfigMasked | null;
  loading: boolean;
}) {
  const testedAt = formatStamp(config?.last_tested_at);
  return (
    <CardShell icon="TEST" gradient="from-brand-500 to-indigo-500" label="Last test email">
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <>
          <div className="text-lg font-bold text-neutral-900 dark:text-white">
            {config?.last_test_status ?? 'Never'}
          </div>
          <div className="mt-1 text-xs tabular-nums text-neutral-500 dark:text-neutral-400">
            {testedAt ?? 'Send one once the account is verified.'}
          </div>
          {config?.last_test_error && (
            <div className="mt-2 break-all rounded-md bg-danger-50 p-2 font-mono text-[11px] text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
              {config.last_test_error}
            </div>
          )}
        </>
      )}
    </CardShell>
  );
}

function EmailGlyph() {
  return (
    <svg
      className="hidden h-14 w-14 text-white/80 md:block"
      fill="none"
      viewBox="0 0 48 48"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 12h36a2 2 0 012 2v20a2 2 0 01-2 2H6a2 2 0 01-2-2V14a2 2 0 012-2z" />
      <path d="M6 14l18 14L42 14" />
    </svg>
  );
}
